import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import { toLocalDatetimeInput, formatAppDate, toLocalDateKey } from '../../utils/dateUtils';
import TaskSplitDialog from '../../components/Tasks/TaskSplitDialog';
import ShiftHandoffModal from '../../components/Tasks/ShiftHandoffModal';
import SupervisorApprovalDialog from '../../components/Tasks/SupervisorApprovalDialog';
import EntityLink from '../../components/Common/EntityLink';
import BackButton from '../../components/Common/BackButton';
import RequisitionDetailPanel from '../../components/Inventory/RequisitionDetailPanel';
import QualityCheckTaskInterface from '../../components/Tasks/TypeSpecific/QualityCheckTaskInterface';
import { useTaskTypes } from '../../contexts/TaskTypesContext';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, hasRole } from '../../utils/roleUtils';
import './Tasks.css';
import '../../components/Tasks/TaskSplitDialog.css';

const ViewTask = forwardRef(({ taskId, onBack, backLabel, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { getTaskTypeOptions: getTaskTypeOptionsFromContext } = useTaskTypes();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [showShiftHandoffModal, setShowShiftHandoffModal] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [showAdminReturnModal, setShowAdminReturnModal] = useState(false);
  const [adminIssuedMaterials, setAdminIssuedMaterials] = useState([]);
  const [adminReturnItems, setAdminReturnItems] = useState([]);
  const [adminReturnLoading, setAdminReturnLoading] = useState(false);

  // Drill-down tab data (lazy loaded)
  const [sessions, setSessions] = useState(null);
  const [rolls, setRolls] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [wasteRecord, setWasteRecord] = useState(null);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState(null);

  const { showSuccess, showError, showWarning } = useMessages();

  const generateTaskCode = () => {
    const date = new Date();
    const dateStr = toLocalDateKey(date).replace(/-/g, '');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `TSK-${dateStr}-${random}`;
  };

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}`));
      if (response.ok) {
        const data = await response.json();
        setTask(data);
        setEditData(data);
      } else {
        setError(t('productionTasks.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching task:', error);
      setError(t('productionTasks.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useImperativeHandle(ref, () => ({
    handleComplete,
    handleSave
  }));

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
    fetchProductionOrders();
    fetchMachines();
    if (taskId && !createMode) {
      // Don't refetch if we just saved this task
      if (!justSaved) {
        fetchTask();
      } else {
        // Reset the flag after the effect runs
        setJustSaved(false);
      }
    } else if (createMode) {
      const newTask = {
        task_code: generateTaskCode(),
        task_name: '',
        description: '',
        task_type: 'AD_HOC',
        priority: 'NORMAL',
        status: 'PENDING',
        department_id: null,
        machine_id: null,
        estimated_duration_minutes: 0,
        scheduled_start: null,
        scheduled_end: null,
        instructions: '',
        production_order_line_id: null
      };
      setTask(newTask);
      setEditData(newTask);
    }
  }, [taskId, createMode, fetchTask]);

  // Clear justSaved flag when taskId changes (switching to different task)
  useEffect(() => {
    setJustSaved(false);
  }, [taskId]);

  const fetchDepartments = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/workflows/departments'));
      if (response.ok) {
        const data = await response.json();
        setDepartments(data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };


  const fetchUsers = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/users/'));
      if (response.ok) {
        const data = await response.json();
        setUsers(data || []);
      } else {
        showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/machines'));
      if (response.ok) {
        const data = await response.json();
        setMachines(data.machines || data || []);
      }
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const fetchProductionOrders = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/production/orders?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        const orders = data.orders || data || [];

        // Fetch lines for each order
        const ordersWithLines = await Promise.all(
          orders.map(async (order) => {
            try {
              const linesResponse = await apiFetch(getApiUrl(`/api/production/orders/${order.po_number}/lines`));
              if (linesResponse.ok) {
                const linesData = await linesResponse.json();
                return {
                  ...order,
                  lines: linesData.lines || linesData || []
                };
              }
            } catch (err) {
              console.error(`Error fetching lines for ${order.po_number}:`, err);
            }
            return { ...order, lines: [] };
          })
        );

        setProductionOrders(ordersWithLines);
      }
    } catch (error) {
      console.error('Error fetching production orders:', error);
    }
  };


  const fetchSessions = useCallback(async () => {
    if (!taskId || sessions !== null) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/sessions`));
      setSessions(res.ok ? await res.json() : []);
    } catch { setSessions([]); }
  }, [taskId, sessions]);

  const fetchRolls = useCallback(async () => {
    if (!taskId || rolls !== null) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/rolls`));
      setRolls(res.ok ? await res.json() : []);
    } catch { setRolls([]); }
  }, [taskId, rolls]);

  const fetchMaterials = useCallback(async () => {
    if (!taskId || materials !== null) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/inventory-requisitions/task/${taskId}/materials-summary`));
      if (res.ok) {
        const data = await res.json();
        setMaterials(data.materials || []);
      } else {
        setMaterials([]);
      }
    } catch { setMaterials([]); }
  }, [taskId, materials]);

  const fetchWaste = useCallback(async () => {
    if (!taskId || wasteRecord !== null) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/waste`));
      setWasteRecord(res.ok ? await res.json() : {});
    } catch { setWasteRecord({}); }
  }, [taskId, wasteRecord]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'shifts') fetchSessions();
    if (tab === 'rolls') fetchRolls();
    if (tab === 'materials') fetchMaterials();
    if (tab === 'waste') { fetchWaste(); fetchSessions(); }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      const url = createMode
        ? getApiUrl('/api/production-tasks/tasks')
        : getApiUrl(`/api/production-tasks/tasks/${taskId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const savedData = await response.json();
        setTask(savedData);
        setEditData(savedData);
        setJustSaved(true);

        showSuccess(
          createMode ? t('productionTasks.view.messages.taskCreated') : t('productionTasks.view.messages.taskUpdated'),
          createMode ? t('productionTasks.view.messages.taskCreatedDesc') : t('productionTasks.view.messages.taskUpdatedDesc')
        );

        window.dispatchEvent(new CustomEvent('task-updated', { detail: savedData }));

        if (onSave) onSave(savedData);
      } else {
        const parsed = await showApiError(response, showError, tCommon);
        setError(parsed.message);
        return;
      }
    } catch (error) {
      console.error('Error saving task:', error);
      setError(t('productionTasks.view.error.savingError'));
      showError(
        createMode ? t('productionTasks.view.messages.taskCreateFailed') : t('productionTasks.view.messages.taskUpdateFailed'),
        t('productionTasks.view.error.savingError')
      );
    }
  };

  const handleComplete = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/status?status=COMPLETED`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        showSuccess(
          t('productionTasks.view.toasts.markedCompleteTitle'),
          t('productionTasks.view.toasts.markedCompleteMessage')
        );
        await fetchTask();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error completing task:', error);
      showError(t('productionTasks.view.toasts.markCompleteFailedTitle'), t('productionTasks.view.toasts.markCompleteNetworkError'));
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(task);
      if (onCancel) onCancel();
    }
  };

  const handleSplitTask = () => {
    setSplitDialogOpen(true);
  };

  const handleSplitComplete = (splitResult) => {
    // Refresh the task to show it's been cancelled/split
    fetchTask();
    showSuccess(
      t('taskSplit.messages.splitComplete'),
      t('taskSplit.messages.splitCompleteDesc', {
        taskCode: task.task_code,
        count: splitResult.total_splits
      })
    );
  };

  const canSplitTask = () => {
    return task && !createMode && !editMode &&
           !['COMPLETED', 'CANCELLED'].includes(task.status);
  };

  const handleAdminReturnOpen = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/task/${taskId}/issued-materials`));
      if (response.ok) {
        const materials = await response.json();
        if (!materials || materials.length === 0) {
          showWarning(t('productionTasks.view.toasts.noMaterialsTitle'), t('productionTasks.view.toasts.noMaterialsMessage'));
          return;
        }
        setAdminIssuedMaterials(materials);
        setAdminReturnItems([{ material_key: '', quantity_returned: '', notes: '' }]);
        setShowAdminReturnModal(true);
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (err) {
      console.error('Error fetching issued materials:', err);
      showError(tCommon('ui.error'), t('productionTasks.view.toasts.loadIssuedFailed'));
    }
  };

  const handleAdminReturnItemChange = (index, field, value) => {
    const updated = [...adminReturnItems];
    updated[index][field] = value;
    setAdminReturnItems(updated);
  };

  const handleAdminReturnAddItem = () => {
    setAdminReturnItems([...adminReturnItems, { material_key: '', quantity_returned: '', notes: '' }]);
  };

  const handleAdminReturnRemoveItem = (index) => {
    setAdminReturnItems(adminReturnItems.filter((_, i) => i !== index));
  };

  // Issued materials come one row per requisition; a return belongs to one requisition
  const issuedMaterialKey = (mat) => `${mat.requisition_id}:${mat.inventory_item_id}`;

  const handleAdminReturnSubmit = async () => {
    const validItems = adminReturnItems.filter(
      item => item.material_key && parseFloat(item.quantity_returned) > 0
    );
    if (validItems.length === 0) {
      showWarning(tCommon('ui.invalidInput'), t('productionTasks.view.toasts.returnNoItems'));
      return;
    }
    // One return per requisition, like the tablet
    const byRequisition = {};
    validItems.forEach(item => {
      const [requisitionId] = item.material_key.split(':');
      (byRequisition[requisitionId] = byRequisition[requisitionId] || []).push(item);
    });
    setAdminReturnLoading(true);
    const created = [];
    try {
      for (const [requisitionId, reqItems] of Object.entries(byRequisition)) {
        const requestBody = {
          requisition_id: parseInt(requisitionId),
          production_task_id: taskId,
          production_order_id: editData.production_order_id,
          return_reason: 'Admin return of unused materials (task completed)',
          items: reqItems.map(item => ({
            inventory_item_id: parseInt(item.material_key.split(':')[1]),
            quantity_returned: parseFloat(item.quantity_returned),
            notes: item.notes || null
          }))
        };
        const response = await apiFetch(getApiUrl(`/api/material-returns/create?returned_by=${user.user_id}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
        if (!response.ok) {
          if (created.length) showWarning(t('productionTasks.view.toasts.partiallyCreatedTitle'), t('productionTasks.view.toasts.partiallyCreatedMessage', { numbers: created.join(', ') }));
          await showApiError(response, showError, tCommon);
          return;
        }
        const result = await response.json();
        created.push(result.return_number);
        // Drop what was already sent so a retry does not duplicate it
        setAdminReturnItems(prev => prev.filter(item => !item.material_key.startsWith(`${requisitionId}:`)));
      }
      showSuccess(t('productionTasks.view.toasts.returnCreatedTitle'), t('productionTasks.view.toasts.returnCreatedMessage', { numbers: created.join(', ') }));
      setShowAdminReturnModal(false);
    } catch (err) {
      console.error('Error creating material return:', err);
      showError(tCommon('ui.error'), t('productionTasks.view.toasts.returnCreateFailed'));
    } finally {
      setAdminReturnLoading(false);
    }
  };

  const getPriorityOptions = () => ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
  // COMPLETED is set by Mark Complete / the operator screens, never by editing (#389): it is only
  // listed when the task already has it
  const getStatusOptions = () => [
    'PENDING', 'ASSIGNED', 'IN_PROGRESS', 'PAUSED', ...(task?.status === 'COMPLETED' ? ['COMPLETED'] : []), 'CANCELLED',
  ];
  const getTaskTypeOptions = () => getTaskTypeOptionsFromContext().map(opt => opt.value);

  const renderDetailsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productionTasks.view.sections.taskInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('productionTasks.view.fields.taskCode')}</label>
            <input
              type="text"
              value={editData.task_code || ''}
              onChange={(e) => handleFieldChange('task_code', e.target.value)}
              disabled={!createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.taskName')} *</label>
            <input
              type="text"
              value={editData.task_name || ''}
              onChange={(e) => handleFieldChange('task_name', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('productionTasks.view.placeholders.taskName')}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.taskType')}</label>
            <select
              value={editData.task_type || 'AD_HOC'}
              onChange={(e) => handleFieldChange('task_type', e.target.value)}
              disabled={!editMode && !createMode}
            >
              {getTaskTypeOptions().map(type => (
                <option key={type} value={type}>{t(`productionTasks.view.taskTypes.${type.toLowerCase()}`)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.priority')}</label>
            <select
              value={editData.priority || 'NORMAL'}
              onChange={(e) => handleFieldChange('priority', e.target.value)}
              disabled={!editMode && !createMode}
            >
              {getPriorityOptions().map(priority => (
                <option key={priority} value={priority}>{t(`productionTasks.view.priority.${priority.toLowerCase()}`)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.status')}</label>
            <select
              value={editData.status || 'PENDING'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!editMode && !createMode}
            >
              {getStatusOptions().map(status => (
                <option key={status} value={status}>{t(`productionTasks.view.status.${status.toLowerCase()}`)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.department')}</label>
            <select
              value={editData.department_id || ''}
              onChange={(e) => handleFieldChange('department_id', parseInt(e.target.value) || null)}
              disabled={!editMode && !createMode}
            >
              <option value="" disabled hidden>{t('productionTasks.view.placeholders.selectDepartment')}</option>
              {departments.map(dept => (
                <option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.assignedTo')}</label>
            <select
              value={(editMode || createMode ? editData.assigned_to : task.assigned_to) || ''}
              onChange={(e) => handleFieldChange('assigned_to', parseInt(e.target.value) || null)}
              disabled={!editMode && !createMode}
            >
              <option value="" disabled hidden>{t('productionTasks.view.placeholders.selectUser')}</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.first_name} {user.last_name} ({user.username})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.estimatedDuration')}</label>
            <input
              type="number"
              value={editData.estimated_duration_minutes || ''}
              onChange={(e) => handleFieldChange('estimated_duration_minutes', parseInt(e.target.value) || 0)}
              disabled={!editMode && !createMode}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.scheduledStart')}</label>
            <input
              type="datetime-local"
              value={toLocalDatetimeInput(editData.scheduled_start)}
              onChange={(e) => {
                const val = e.target.value;
                handleFieldChange('scheduled_start', val ? new Date(val).toISOString() : null);
                // Auto-calculate scheduled_end from duration
                if (val && editData.estimated_duration_minutes) {
                  const end = new Date(val);
                  end.setMinutes(end.getMinutes() + (editData.estimated_duration_minutes || 60));
                  handleFieldChange('scheduled_end', end.toISOString());
                }
              }}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.scheduledEnd')}</label>
            <input
              type="datetime-local"
              value={toLocalDatetimeInput(editData.scheduled_end)}
              onChange={(e) => {
                const val = e.target.value;
                handleFieldChange('scheduled_end', val ? new Date(val).toISOString() : null);
              }}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.machine')}</label>
            <select
              value={editData.machine_id || ''}
              onChange={(e) => handleFieldChange('machine_id', parseInt(e.target.value) || null)}
              disabled={!editMode && !createMode}
            >
              <option value="">-- {t('productionTasks.view.fields.machine')} --</option>
              {machines.map(machine => (
                <option key={machine.machine_id} value={machine.machine_id}>
                  {machine.machine_name} ({machine.machine_code})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('productionTasks.view.fields.description')}</label>
            <textarea
              value={editData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('productionTasks.view.placeholders.descriptionPlaceholder')}
              rows="3"
            />
          </div>
          <div className="form-group full-width">
            <label>{t('productionTasks.view.fields.instructions')}</label>
            <textarea
              value={editData.instructions || ''}
              onChange={(e) => handleFieldChange('instructions', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('productionTasks.view.placeholders.instructionsPlaceholder')}
              rows="4"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderExecutionTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productionTasks.view.sections.executionDetails')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('productionTasks.view.fields.actualStart')}</label>
            <input
              type="text"
              value={task?.actual_start ? formatAppDate(task.actual_start, 'datetime') : t('productionTasks.view.defaultValues.notStarted')}
              disabled
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.actualEnd')}</label>
            <input
              type="text"
              value={task?.actual_end ? formatAppDate(task.actual_end, 'datetime') : t('productionTasks.view.defaultValues.notCompleted')}
              disabled
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.actualDuration')}</label>
            <input
              type="text"
              value={task?.actual_duration_minutes || t('productionTasks.view.defaultValues.notAvailable')}
              disabled
            />
          </div>
          <div className="form-group">
            <label>{t('productionTasks.view.fields.qualityRating')}</label>
            <input
              type="number"
              value={editData.quality_rating || ''}
              onChange={(e) => handleFieldChange('quality_rating', parseInt(e.target.value) || null)}
              disabled={!editMode}
              min="1"
              max="10"
              placeholder={t('productionTasks.view.placeholders.qualityRatingPlaceholder')}
            />
          </div>
          <div className="form-group full-width">
            <label>{t('productionTasks.view.fields.completionNotes')}</label>
            <textarea
              value={editData.completion_notes || ''}
              onChange={(e) => handleFieldChange('completion_notes', e.target.value)}
              disabled={!editMode}
              placeholder={t('productionTasks.view.placeholders.completionNotesPlaceholder')}
              rows="4"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('productionTasks.view.sections.productionInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('productionTasks.view.fields.productionOrderLine')}</label>
            {(editMode || createMode) ? (
              <select
                value={editData.production_order_line_id || ''}
                onChange={(e) => handleFieldChange('production_order_line_id', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="" disabled hidden>{t('productionTasks.view.placeholders.selectProductionOrderLine')}</option>
                {productionOrders.map(order => (
                  <optgroup key={order.production_order_id} label={`${order.po_number} - ${order.customer_name || t('productionTasks.view.defaultValues.notAvailable')}`}>
                    {order.lines && order.lines.map(line => (
                      <option key={line.production_order_line_id} value={line.production_order_line_id}>
                        {t('productionTasks.view.lineOption', { number: line.line_number, product: line.product_name, quantity: line.order_quantity, uom: line.uom_code })}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={(() => {
                  if (!task.production_order_line_id) return t('productionTasks.view.defaultValues.notAvailable');

                  // Find the selected line in productionOrders
                  for (const order of productionOrders) {
                    if (!order.lines) continue;
                    const line = order.lines.find(l => l.production_order_line_id === task.production_order_line_id);
                    if (line) {
                      return t('productionTasks.view.lineValue', { po: order.po_number, number: line.line_number, product: line.product_name });
                    }
                  }
                  return t('productionTasks.view.lineId', { id: task.production_order_line_id });
                })()}
                disabled
              />
            )}
          </div>
          {task?.workflow_instance_id && (
            <div className="form-group">
              <label>{t('productionTasks.view.fields.workflowInstance')}</label>
              <input
                type="text"
                value={`WF-${task.workflow_instance_id}`}
                disabled
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderContextBar = () => {
    if (createMode || !task) return null;
    const hasPO = task.po_number && task.production_order_id;
    const hasQuote = task.quotation_id && task.quotation_number;
    const hasSO = task.sales_order_id && task.sales_order_number;
    const hasCustomer = task.customer_name;
    if (!hasPO && !hasQuote && !hasSO && !hasCustomer) return null;
    return (
      <div className="task-context-bar">
        {hasCustomer && (
          <span className="context-item">
            <span className="context-label">{tCommon('ui.customer')}:</span>
            <span>{task.customer_name}</span>
          </span>
        )}
        {hasPO && (
          <span className="context-item">
            <span className="context-label">{t('productionTasks.view.contextBar.order')}:</span>
            <EntityLink type="production-order" id={task.po_number || task.production_order_id} label={task.po_number} returnLabel={task.task_code} returnType="task" returnId={taskId} />
          </span>
        )}
        {hasQuote && (
          <span className="context-item">
            <span className="context-label">{t('productionTasks.view.contextBar.quote')}:</span>
            <EntityLink type="quote" id={task.quotation_id} label={task.quotation_number} returnLabel={task.task_code} returnType="task" returnId={taskId} />
          </span>
        )}
        {hasSO && (
          <span className="context-item">
            <span className="context-label">{t('productionTasks.view.contextBar.salesOrder')}:</span>
            <EntityLink type="sales-order" id={task.sales_order_id} label={task.sales_order_number} returnLabel={task.task_code} returnType="task" returnId={taskId} />
          </span>
        )}
      </div>
    );
  };

  const renderShiftsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productionTasks.view.shifts.title')}</h3>
        {sessions === null ? (
          <div className="loading">{t('productionTasks.view.shifts.loading')}</div>
        ) : sessions.length === 0 ? (
          <div className="no-data" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('productionTasks.view.shifts.empty')}</div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('productionTasks.view.shifts.columns.operator')}</th>
                <th>{tCommon('ui.date')}</th>
                <th>{t('productionTasks.view.shifts.columns.shift')}</th>
                <th>{t('productionTasks.view.shifts.columns.output')}</th>
                <th>{t('productionTasks.view.shifts.columns.wasteKg')}</th>
                <th>{t('productionTasks.view.fields.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s, i) => (
                <tr key={s.session_id || i}>
                  <td>{s.session_sequence || i + 1}</td>
                  <td>{s.operator_name || s.operator_username || '-'}</td>
                  <td>{s.session_date ? formatAppDate(s.session_date) : '-'}</td>
                  <td>{s.shift_type ? t(`operator:printing.shiftTypes.${s.shift_type}`, { defaultValue: s.shift_type }) : '-'}</td>
                  <td>{s.quantity_produced != null ? `${s.quantity_produced} ${s.uom || ''}` : '-'}</td>
                  <td>{s.waste_kg != null ? parseFloat(s.waste_kg).toFixed(3) : '-'}</td>
                  <td>{s.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <td colSpan={4}>{tCommon('ui.total')}</td>
                <td>{sessions.reduce((sum, s) => sum + (parseFloat(s.quantity_produced) || 0), 0).toFixed(3)}</td>
                <td>{sessions.reduce((sum, s) => sum + (parseFloat(s.waste_kg) || 0), 0).toFixed(3)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );

  const renderRollsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productionTasks.view.rolls.title')}</h3>
        {rolls === null ? (
          <div className="loading">{t('productionTasks.view.rolls.loading')}</div>
        ) : rolls.length === 0 ? (
          <div className="no-data" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('productionTasks.view.rolls.empty')}</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <div className="summary-stat"><strong>{rolls.length}</strong><span>{t('productionTasks.view.rolls.rolls')}</span></div>
              <div className="summary-stat"><strong>{rolls.reduce((s, r) => s + (parseFloat(r.initial_weight_kg) || 0), 0).toFixed(2)} kg</strong><span>{t('productionTasks.view.waste.totalInput')}</span></div>
              <div className="summary-stat"><strong>{rolls.reduce((s, r) => s + (parseFloat(r.waste_weight_kg) || 0), 0).toFixed(2)} kg</strong><span>{t('productionTasks.view.waste.totalWaste')}</span></div>
              <div className="summary-stat"><strong>{rolls.reduce((s, r) => s + (parseFloat(r.final_meters) || 0), 0).toFixed(2)} m</strong><span>{t('productionTasks.view.rolls.totalMeters')}</span></div>
            </div>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('productionTasks.view.rolls.columns.barcode')}</th>
                  <th>{t('productionTasks.view.rolls.columns.initialKg')}</th>
                  <th>{t('productionTasks.view.rolls.columns.finalKg')}</th>
                  <th>{t('productionTasks.view.rolls.columns.wasteKg')}</th>
                  <th>{t('productionTasks.view.rolls.columns.meters')}</th>
                  <th>{t('productionTasks.view.rolls.columns.category')}</th>
                </tr>
              </thead>
              <tbody>
                {rolls.map((r, i) => (
                  <tr key={r.roll_id || i}>
                    <td>{r.roll_sequence || i + 1}</td>
                    <td>{r.roll_barcode || '-'}</td>
                    <td>{r.initial_weight_kg != null ? parseFloat(r.initial_weight_kg).toFixed(3) : '-'}</td>
                    <td>{r.final_weight_kg != null ? parseFloat(r.final_weight_kg).toFixed(3) : '-'}</td>
                    <td>{r.waste_weight_kg != null ? parseFloat(r.waste_weight_kg).toFixed(3) : '-'}</td>
                    <td>{r.final_meters != null ? parseFloat(r.final_meters).toFixed(0) : '-'}</td>
                    <td>{r.waste_category ? t(`wasteReviewDashboard.categories.${r.waste_category}`, { defaultValue: r.waste_category }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );

  const renderMaterialsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productionTasks.view.materials.title')}</h3>
        {materials === null ? (
          <div className="loading">{t('productionTasks.view.materials.loading')}</div>
        ) : materials.length === 0 ? (
          <div className="no-data" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('productionTasks.view.materials.empty')}</div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{t('productionTasks.view.materials.columns.itemCode')}</th>
                <th>{t('productionTasks.view.materials.columns.itemName')}</th>
                <th>{t('productionTasks.view.materials.columns.bomQty')}</th>
                <th>{t('productionTasks.view.materials.columns.requested')}</th>
                <th>{t('productionTasks.view.materials.columns.issued')}</th>
                <th>{t('productionTasks.view.materials.columns.returned')}</th>
                <th>{t('productionTasks.view.materials.columns.netUsed')}</th>
                <th>{tCommon('ui.status')}</th>
                <th>{t('productionTasks.view.materials.columns.requisition')}</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m, i) => {
                const net = (parseFloat(m.total_issued) || 0) - (parseFloat(m.total_returned) || 0);
                const statusClass = m.status === 'FULFILLED' ? 'status-success' :
                                    m.status === 'PARTIAL' ? 'status-warning' :
                                    m.status === 'OVER' ? 'status-error' :
                                    m.status === 'PENDING' ? 'status-warning' : 'status-inactive';
                return (
                  <tr key={m.inventory_item_id || i}>
                    <td>{m.item_code || '-'}</td>
                    <td>{m.item_name || '-'}</td>
                    <td>{m.bom_quantity != null ? parseFloat(m.bom_quantity).toFixed(3) : '-'}</td>
                    <td>{m.total_requested != null ? parseFloat(m.total_requested).toFixed(3) : '-'}</td>
                    <td>{m.total_issued != null ? parseFloat(m.total_issued).toFixed(3) : '-'}</td>
                    <td>{m.total_returned != null ? parseFloat(m.total_returned).toFixed(3) : '0.000'}</td>
                    <td><strong>{net.toFixed(3)}</strong></td>
                    <td><span className={`status-badge ${statusClass}`}>{m.status ? t(`productionTasks.view.materials.status.${m.status}`, { defaultValue: m.status }) : '-'}</span></td>
                    <td>
                      {m.requisition_id ? (
                        <button
                          className="entity-link"
                          onClick={() => setSelectedRequisitionId(m.requisition_id)}
                          title={t('productionTasks.view.materials.viewRequisition')}
                        >
                          {m.requisition_number || `REQ-${m.requisition_id}`} →
                        </button>
                      ) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderWasteTab = () => {
    const hasFormalRecord = wasteRecord && wasteRecord.waste_record_id;
    const sessionWasteTotal = (sessions || []).reduce((sum, s) => sum + (parseFloat(s.waste_kg) || 0), 0);
    const isLoading = wasteRecord === null || (!hasFormalRecord && sessions === null);

    return (
      <div className="tab-content">
        <div className="form-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>{t('productionTasks.view.waste.title')}</h3>
            {hasFormalRecord && (
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                {t('productionTasks.view.waste.reviewAvailableIn')} <strong>{t('productionTasks.view.waste.reviewLocation')}</strong>
              </span>
            )}
          </div>
          {isLoading ? (
            <div className="loading">{t('productionTasks.view.waste.loading')}</div>
          ) : hasFormalRecord ? (
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div className="info-item">
                <span className="info-label">{t('productionTasks.view.waste.totalInput')}</span>
                <span className="info-value">{wasteRecord.total_input_kg != null ? `${parseFloat(wasteRecord.total_input_kg).toFixed(3)} kg` : '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('productionTasks.view.waste.totalWaste')}</span>
                <span className="info-value">{wasteRecord.total_waste_kg != null ? `${parseFloat(wasteRecord.total_waste_kg).toFixed(3)} kg` : '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('productionTasks.view.waste.wastePercent')}</span>
                <span className="info-value">{wasteRecord.waste_percentage != null ? `${parseFloat(wasteRecord.waste_percentage).toFixed(1)}%` : '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('productionTasks.view.waste.primaryCategory')}</span>
                <span className="info-value">{wasteRecord.primary_waste_category ? t(`wasteReviewDashboard.categories.${wasteRecord.primary_waste_category}`, { defaultValue: wasteRecord.primary_waste_category }) : '-'}</span>
              </div>
              {wasteRecord.review_status && (
                <div className="info-item">
                  <span className="info-label">{t('productionTasks.view.waste.reviewStatus')}</span>
                  <span className="info-value">
                    <span className={`status-badge status-${wasteRecord.review_status?.toLowerCase()}`}>{t(`productionTasks.view.waste.reviewStatuses.${wasteRecord.review_status}`, { defaultValue: wasteRecord.review_status })}</span>
                  </span>
                </div>
              )}
            </div>
          ) : sessionWasteTotal > 0 ? (
            <>
              <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div className="info-item">
                  <span className="info-label">{t('productionTasks.view.waste.totalWasteFromShifts')}</span>
                  <span className="info-value">{sessionWasteTotal.toFixed(3)} kg</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('productionTasks.view.waste.shiftsWithWaste')}</span>
                  <span className="info-value">{(sessions || []).filter(s => parseFloat(s.waste_kg) > 0).length}</span>
                </div>
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                {t('productionTasks.view.waste.derivedFromShifts')}
              </div>
            </>
          ) : (
            <div className="no-data" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('productionTasks.view.waste.empty')}</div>
          )}
        </div>
      </div>
    );
  };

  const renderQCTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productionTasks.view.qualityCheck')}</h3>
        <QualityCheckTaskInterface
          task={task}
          readOnly={true}
        />
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('productionTasks.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!task && !createMode) {
    return <div className="view-container"><div className="loading">{t('productionTasks.view.noTaskFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <BackButton onBack={onBack} entityType="task" entityIds={[taskId, editData?.task_id]} fallbackLabel={backLabel} />
          <h1>
            {createMode ? t('productionTasks.view.newTask') : `${editData.task_code} - ${editData.task_name}`}
          </h1>
          {!createMode && (
            <span className={`status-badge status-${editData.status?.toLowerCase()}`}>
              {t(`productionTasks.view.status.${editData.status?.toLowerCase()}`)}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('productionTasks.view.buttons.edit')}</button>
              {canSplitTask() && (
                <button className="btn-warning" onClick={handleSplitTask}>{t('productionTasks.view.buttons.splitTask')}</button>
              )}
              {editData.status === 'IN_PROGRESS' && (
                <button className="btn-warning" onClick={() => setShowShiftHandoffModal(true)}>{t('productionTasks.view.buttons.endMyShift')}</button>
              )}
              {editData.status !== 'COMPLETED' && (
                <button className="btn-success" onClick={handleComplete}>{t('productionTasks.view.buttons.markComplete')}</button>
              )}
              {editData.status === 'COMPLETED' && (
                <>
                  {isAdmin(user) && (
                    <button className="btn-warning" onClick={handleAdminReturnOpen}>
                      ↩ {t('productionTasks.view.buttons.returnMaterials')}
                    </button>
                  )}
                  {editData.completion_status === 'PENDING_APPROVAL' && (
                    <>
                      {/* approve-quantity is SUPERVISOR/MANAGER (backend/common/rbac.py) */}
                      {(hasRole(user, 'SUPERVISOR', 'MANAGER') || isAdmin(user)) && (
                        <button className="btn-success" onClick={() => setApprovalDialogOpen(true)}>
                          {t('productionTasks.view.buttons.approveQuantity')}
                        </button>
                      )}
                      <span className="status-info">
                        ✓ {t('productionTasks.view.completedPendingApproval')}
                      </span>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('productionTasks.view.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('productionTasks.view.buttons.create') : t('productionTasks.view.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>

      {renderContextBar()}

      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => handleTabChange('details')}
          >
            {t('productionTasks.view.tabs.details')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'execution' ? 'active' : ''}`}
              onClick={() => handleTabChange('execution')}
            >
              {t('productionTasks.view.tabs.execution')}
            </button>
          )}
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'shifts' ? 'active' : ''}`}
              onClick={() => handleTabChange('shifts')}
            >
              {t('productionTasks.view.tabs.shifts')}
            </button>
          )}
          {!createMode && task?.task_type === 'PRINTING' && (
            <button
              className={`tab-item ${activeTab === 'rolls' ? 'active' : ''}`}
              onClick={() => handleTabChange('rolls')}
            >
              {t('productionTasks.view.tabs.rolls')}
            </button>
          )}
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'materials' ? 'active' : ''}`}
              onClick={() => handleTabChange('materials')}
            >
              {t('productionTasks.view.tabs.materials')}
            </button>
          )}
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'waste' ? 'active' : ''}`}
              onClick={() => handleTabChange('waste')}
            >
              {t('productionTasks.view.tabs.waste')}
            </button>
          )}
          {!createMode && task?.task_type === 'QUALITY_CHECK' && (
            <button
              className={`tab-item ${activeTab === 'qc' ? 'active' : ''}`}
              onClick={() => handleTabChange('qc')}
            >
              {t('productionTasks.view.tabs.qc')}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'details' && renderDetailsTab()}
          {activeTab === 'execution' && !createMode && renderExecutionTab()}
          {activeTab === 'shifts' && !createMode && renderShiftsTab()}
          {activeTab === 'rolls' && !createMode && renderRollsTab()}
          {activeTab === 'materials' && !createMode && renderMaterialsTab()}
          {activeTab === 'waste' && !createMode && renderWasteTab()}
          {activeTab === 'qc' && !createMode && renderQCTab()}
        </div>
      </div>

      <TaskSplitDialog
        isOpen={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        task={task}
        onSplitComplete={handleSplitComplete}
      />

      <SupervisorApprovalDialog
        isOpen={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        task={task}
        onApprovalComplete={fetchTask}
      />

      <ShiftHandoffModal
        isOpen={showShiftHandoffModal}
        onClose={() => setShowShiftHandoffModal(false)}
        taskId={taskId}
        taskType={editData.task_type}
        onHandoffComplete={fetchTask}
        taskContext={task}
      />

      {selectedRequisitionId && (
        <RequisitionDetailPanel
          requisitionId={selectedRequisitionId}
          onClose={() => setSelectedRequisitionId(null)}
        />
      )}

      {showAdminReturnModal && (
        <div className="modal-overlay" onClick={() => setShowAdminReturnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>↩ {t('productionTasks.view.adminReturn.title', { code: editData.task_code })}</h2>
              <button className="modal-close" onClick={() => setShowAdminReturnModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              {editData.po_number && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>{t('productionTasks.view.fields.productionOrder')}:</strong> {editData.po_number}
                </div>
              )}
              <div style={{
                background: 'var(--color-info-bg, #e8f4fd)',
                border: '1px solid var(--color-info-border, #90caf9)',
                borderRadius: '4px',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.9em',
                color: 'var(--color-info-text, #1565c0)'
              }}>
                {t('productionTasks.view.adminReturn.hint')}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <strong>{t('productionTasks.view.adminReturn.materialsToReturn')}:</strong>
                  <button className="btn btn-sm btn-secondary" onClick={handleAdminReturnAddItem}>
                    ➕ {t('productionTasks.view.adminReturn.addMaterial')}
                  </button>
                </div>

                {adminReturnItems.map((item, index) => {
                  const selectedMaterial = adminIssuedMaterials.find(m => issuedMaterialKey(m) === item.material_key);
                  const maxQty = selectedMaterial ? parseFloat(selectedMaterial.total_issued) : null;

                  return (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 2fr 40px',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <select
                          value={item.material_key}
                          onChange={(e) => handleAdminReturnItemChange(index, 'material_key', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-primary)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          <option value="" disabled hidden>{t('productionTasks.view.adminReturn.selectMaterial')}</option>
                          {adminIssuedMaterials.map(mat => (
                            <option key={issuedMaterialKey(mat)} value={issuedMaterialKey(mat)}>
                              {mat.item_code} - {mat.item_name} ({mat.requisition_number})
                            </option>
                          ))}
                        </select>
                        {selectedMaterial && (
                          <div style={{ fontSize: '0.75em', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            {t('productionTasks.view.adminReturn.issued', { quantity: parseFloat(selectedMaterial.total_issued).toFixed(3), uom: selectedMaterial.unit_of_measure })}
                          </div>
                        )}
                      </div>

                      <input
                        type="number"
                        placeholder={tCommon('ui.quantity')}
                        value={item.quantity_returned}
                        onChange={(e) => handleAdminReturnItemChange(index, 'quantity_returned', e.target.value)}
                        step="0.001"
                        min="0"
                        max={maxQty || undefined}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />

                      <input
                        type="text"
                        placeholder={t('productionTasks.view.adminReturn.notesOptional')}
                        value={item.notes}
                        onChange={(e) => handleAdminReturnItemChange(index, 'notes', e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />

                      {adminReturnItems.length > 1 && (
                        <button
                          onClick={() => handleAdminReturnRemoveItem(index)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            padding: '0.5rem'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdminReturnModal(false)}>
                {t('productionTasks.view.buttons.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAdminReturnSubmit}
                disabled={adminReturnLoading || adminReturnItems.every(i => !i.material_key || !(parseFloat(i.quantity_returned) > 0))}
              >
                {adminReturnLoading ? t('productionTasks.view.adminReturn.submitting') : t('productionTasks.view.adminReturn.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ViewTask.displayName = 'ViewTask';

export default ViewTask;